<template>
  <section id="player-projections" class="projections-section" aria-label="Player Projections">
    <div class="projections-section__inner">
      <div class="section__header">
        <div class="section-number">III</div>
        <div class="section__titles">
          <p class="eyebrow">Per-Player Outlook &mdash; Baseball Cards</p>
          <h2 class="display-lg">{{ heading ?? 'What each player could do.' }}</h2>
          <p v-if="!heading" class="body-lg section__dek">
            Not a point estimate &mdash; a full <em>distribution</em> of possible outcomes for
            tonight's key players. Flip through the deck.
          </p>
        </div>
      </div>

      <hr class="rule rule--double" />

      <div class="projection-grid">
        <PlayerProjectionCard
          v-for="(player, index) in players"
          :key="player.player_id"
          :player="player"
          :index="index"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import PlayerProjectionCard from './PlayerProjectionCard.vue'

interface Player {
  player_id: string | number
  name: string
  team: string
  position: string | null
  mean_hits: number
  mean_hr: number
  mean_rbi: number
  hit_distribution: number[]
  hr_distribution: number[]
}

withDefaults(
  defineProps<{
    players: Player[]
    heading?: string
  }>(),
  { heading: undefined }
)
</script>

<style scoped>
.projections-section {
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 2rem;
  border-top: var(--rule-thin) solid var(--ink);
}

.section__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
  max-width: 960px;
}

.section__titles .eyebrow {
  margin-bottom: 1rem;
}

.section__titles .display-lg em {
  font-style: italic;
  color: var(--ox);
}

.section__dek {
  margin-top: 1.25rem;
  max-width: 58ch;
}

.section__dek em {
  font-style: italic;
  color: var(--ox);
}

.projection-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
}

@media (max-width: 1024px) {
  .projection-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .projections-section {
    padding: 3rem 1rem;
  }

  .section__header {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .projection-grid {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
}
</style>
